// =============================================
// CategoryTree — multi-level category tree with full CRUD (add / rename / sub / delete)
// =============================================
// Shared by the Masters tab AND the Notes-tab sidebar so categories can be
// managed from either place. Clicking a name calls onSelect (filter, Notes) or
// onOpenCategory (jump, Masters).
// =============================================
import React, { useCallback, useMemo, useState } from 'react';
import {
  FiChevronRight, FiChevronDown, FiPlus, FiEdit2, FiTrash2, FiFolder, FiCheck, FiX, FiCornerDownRight,
} from 'react-icons/fi';
import {
  CategoryRow, LinkRow, NotesBundle, CategoryNode,
  buildCategoryTree, createCategory, updateCategory, deleteCategory, entryCountForCategory,
} from './notesLib';

interface Props {
  categories: CategoryRow[];
  links: LinkRow[];
  onReload: () => Promise<NotesBundle>;
  compact?: boolean;
  selectedId?: number | null;
  onSelect?: (id: number) => void;        // click name → filter in place (Notes)
  onOpenCategory?: (id: number) => void;  // click name → jump (Masters)
  onMsg?: (m: string | null) => void;     // surface a guard message to the host
}

const CategoryTree: React.FC<Props> = ({ categories, links, onReload, compact, selectedId, onSelect, onOpenCategory, onMsg }) => {
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [addParent, setAddParent] = useState<number | null | undefined>(undefined); // undefined=closed, null=root
  const [addName, setAddName] = useState('');

  const tree = useMemo(() => buildCategoryTree(categories), [categories]);
  const toggle = (id: number) => setExpanded(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const msg = (m: string | null) => onMsg && onMsg(m);

  const submitAdd = useCallback(async () => {
    const name = addName.trim(); if (!name) return;
    await createCategory(name, addParent ?? null);
    setAddName(''); setAddParent(undefined); msg(null);
    await onReload();
  }, [addName, addParent, onReload]); // eslint-disable-line react-hooks/exhaustive-deps

  const submitRename = useCallback(async () => {
    if (editingId == null) return;
    const name = editName.trim(); if (!name) { setEditingId(null); return; }
    await updateCategory(editingId, { category_name: name });
    setEditingId(null); await onReload();
  }, [editingId, editName, onReload]);

  const remove = useCallback(async (cat: CategoryRow) => {
    const childCount = categories.filter(c => c.parent_category_id == cat.category_id && c.is_deleted != 1).length; // eslint-disable-line eqeqeq
    const entryCount = entryCountForCategory(cat.category_id, links, categories, true);
    if (childCount > 0) { msg(`"${cat.category_name}" has sub-categories — delete or move those first.`); return; }
    if (entryCount > 0) { msg(`"${cat.category_name}" has ${entryCount} ${entryCount === 1 ? 'entry' : 'entries'} — move them first.`); return; }
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete category "${cat.category_name}"?`)) return;
    await deleteCategory(cat.category_id); msg(null); await onReload();
  }, [categories, links, onReload]); // eslint-disable-line react-hooks/exhaustive-deps

  const clickName = (id: number) => { if (onSelect) onSelect(id); else if (onOpenCategory) onOpenCategory(id); };

  const renderNode = (node: CategoryNode, depth: number): React.ReactNode => {
    const hasKids = node.children.length > 0;
    const open = expanded.has(node.category_id);
    const count = entryCountForCategory(node.category_id, links, categories, false);
    return (
      <React.Fragment key={node.category_id}>
        <div className={`kvc-row ${selectedId == node.category_id ? 'sel' : ''}`} style={{ paddingLeft: 8 + depth * (compact ? 14 : 18) }}>{/* eslint-disable-line eqeqeq */}
          <button className="kvc-twist" onClick={() => hasKids && toggle(node.category_id)} aria-label={hasKids ? 'Toggle' : ''}>
            {hasKids ? (open ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />) : <span className="kvc-dot" />}
          </button>
          <FiFolder size={13} style={{ color: node.color || 'var(--gray-400)', flexShrink: 0 }} />
          {editingId === node.category_id ? (
            <span className="kvc-edit">
              <input autoFocus value={editName} onChange={e => setEditName(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') void submitRename(); if (e.key === 'Escape') setEditingId(null); }} maxLength={255} />
              <button onClick={() => void submitRename()} title="Save"><FiCheck size={14} /></button>
              <button onClick={() => setEditingId(null)} title="Cancel"><FiX size={14} /></button>
            </span>
          ) : (
            <button className="kvc-name" onClick={() => clickName(node.category_id)} title="Open entries in this category">
              <span className="kvc-name-text">{node.category_name}</span>
              {count > 0 && <span className="kvc-count">{count}</span>}
            </button>
          )}
          <span className="kvc-actions">
            <button title="Add sub-category" onClick={() => { setAddParent(node.category_id); setAddName(''); setExpanded(p => new Set(p).add(node.category_id)); }}><FiPlus size={13} /></button>
            <button title="Rename" onClick={() => { setEditingId(node.category_id); setEditName(node.category_name); }}><FiEdit2 size={13} /></button>
            <button title="Delete" onClick={() => void remove(node)}><FiTrash2 size={13} /></button>
          </span>
        </div>
        {addParent === node.category_id && (
          <div className="kvc-addrow" style={{ paddingLeft: 8 + (depth + 1) * (compact ? 14 : 18) }}>
            <FiCornerDownRight size={13} />
            <input autoFocus value={addName} onChange={e => setAddName(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') void submitAdd(); if (e.key === 'Escape') setAddParent(undefined); }} placeholder="Sub-category name…" maxLength={255} />
            <button onClick={() => void submitAdd()}>Add</button>
            <button className="kvc-cancel" onClick={() => setAddParent(undefined)}>Cancel</button>
          </div>
        )}
        {open && node.children.map(c => renderNode(c, depth + 1))}
      </React.Fragment>
    );
  };

  return (
    <div className={`kvc-tree ${compact ? 'kvc-compact' : ''}`}>
      <button className="kvc-add-top" onClick={() => { setAddParent(null); setAddName(''); }}><FiPlus size={13} /> New category</button>
      {addParent === null && (
        <div className="kvc-addrow kvc-addroot">
          <FiFolder size={13} />
          <input autoFocus value={addName} onChange={e => setAddName(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') void submitAdd(); if (e.key === 'Escape') setAddParent(undefined); }} placeholder="Top-level category…" maxLength={255} />
          <button onClick={() => void submitAdd()}>Add</button>
          <button className="kvc-cancel" onClick={() => setAddParent(undefined)}>Cancel</button>
        </div>
      )}
      {tree.map(n => renderNode(n, 0))}
    </div>
  );
};

export default CategoryTree;
